/**
 * KOAI 게스트 60초 체험 — 설치/가입 없이 Ephemeral RAG
 * 흐름: 문서 올리기 → 질문 → 출처와 함께 답
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  AutoAwesome as SparkleIcon,
  CloudUpload as UploadIcon,
  Description as DocIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  Lock as LockIcon,
  SaveAlt as SaveIcon,
  Send as SendIcon,
  TableChart as ExcelIcon,
  Slideshow as PptIcon,
} from '@mui/icons-material';
import { ephemeralAPI } from '../services/api';
import { trackEvent } from '../utils/analytics';
import { downloadExportedFile, getExportErrorMessage } from '../utils/exportAnswerDownload';

function createSessionId() {
  return `try_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function StepPill({ active, done, index, label }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        bgcolor: active ? 'primary.main' : done ? 'rgba(25,118,210,0.08)' : 'grey.100',
        color: active ? 'common.white' : done ? 'primary.dark' : 'text.secondary',
        border: '1px solid',
        borderColor: active || done ? 'primary.light' : 'grey.300',
        minWidth: 0,
        flex: 1,
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          bgcolor: active ? 'rgba(255,255,255,0.25)' : done ? 'primary.main' : 'grey.300',
          color: active || done ? 'common.white' : 'text.secondary',
          flexShrink: 0,
        }}
      >
        {index}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} noWrap>
        {label}
      </Typography>
    </Box>
  );
}

function SourceList({ sources }) {
  const [open, setOpen] = useState(true);
  if (!sources?.length) return null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          출처 {sources.length}개
        </Typography>
        {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
      </Box>
      <Collapse in={open}>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {sources.map((source, idx) => (
            <Paper
              key={source.id || idx}
              variant="outlined"
              sx={{ p: 1.25, bgcolor: 'grey.50' }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'primary.main' }}>
                {source.source || `출처 ${idx + 1}`}
                {typeof source.score === 'number' ? ` · 관련도 ${(source.score * 100).toFixed(0)}%` : ''}
              </Typography>
              {source.snippet && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {source.snippet}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function TryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const sampleLoadedRef = useRef(false);

  const [sessionId] = useState(() => createSessionId());
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { progress, message, truncated }
  const [loadingSample, setLoadingSample] = useState(false);
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [exportingKey, setExportingKey] = useState('');
  const chatEndRef = useRef(null);

  const demoQuestions = useMemo(
    () => [
      t('try.demoQ1'),
      t('try.demoQ2'),
      t('try.demoQ3'),
    ],
    [t]
  );

  const hasDocs = documents.length > 0;
  const hasAnswer = messages.some((m) => m.role === 'assistant');
  const activeStep = !hasDocs ? 1 : !hasAnswer ? 2 : 3;

  useEffect(() => {
    trackEvent('try_start', { method: searchParams.get('sample') === '1' ? 'sample_link' : 'direct' });
  }, [searchParams]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, asking]);

  const refreshSession = useCallback(async () => {
    const res = await ephemeralAPI.getSession(sessionId);
    setDocuments(res.data?.data?.documents || []);
  }, [sessionId]);

  const loadSample = useCallback(async () => {
    if (loadingSample || uploading) return;
    setLoadingSample(true);
    setError('');
    try {
      const res = await ephemeralAPI.loadSample(sessionId);
      if (!res.data?.success) {
        throw new Error(res.data?.error || t('try.sampleError'));
      }
      const docs = res.data?.data?.documents || [];
      setDocuments(docs);
      trackEvent('doc_ready', { method: 'sample' });
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('try.sampleError'));
    } finally {
      setLoadingSample(false);
    }
  }, [loadingSample, uploading, sessionId, t]);

  // ?sample=1 이면 자동 샘플 로드
  useEffect(() => {
    if (searchParams.get('sample') === '1' && !sampleLoadedRef.current) {
      sampleLoadedRef.current = true;
      loadSample();
    }
  }, [searchParams, loadSample]);

  // 언마운트 시 세션 정리 (베스트에포트)
  useEffect(() => {
    return () => {
      ephemeralAPI.deleteSession(sessionId).catch(() => {});
    };
  }, [sessionId]);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setUploading(true);
    setError('');
    setInfo('');
    setUploadProgress({ progress: 0, message: t('try.processing') });
    try {
      const res = await ephemeralAPI.upload(files, sessionId, {
        onProgress: (job) => {
          setUploadProgress({
            progress: typeof job.progress === 'number' ? job.progress : 0,
            message: job.message || t('try.processing'),
            truncated: job.truncated,
          });
        },
      });
      if (!res.data?.success) {
        throw new Error(res.data?.error || t('try.uploadError'));
      }
      await refreshSession();
      if (res.data?.truncated) {
        setInfo(t('try.truncatedNote'));
      }
      trackEvent('doc_ready', { method: 'upload' });
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('try.uploadError'));
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const ask = async (q) => {
    const trimmed = (q || '').trim();
    if (!trimmed || asking) return;
    if (!hasDocs) {
      setError(t('try.needDocument'));
      return;
    }

    setAsking(true);
    setError('');
    setQuestion('');
    const userMsg = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const res = await ephemeralAPI.query(sessionId, trimmed, history);
      const data = res.data?.data;
      if (!res.data?.success || !data) {
        throw new Error(res.data?.error || t('try.queryError'));
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.response,
          sources: data.sources || [],
          hasContext: data.hasContext,
        },
      ]);
      trackEvent('answer_view', { has_sources: (data.sources || []).length > 0 });
    } catch (err) {
      setMessages((prev) => prev.slice(0, -1));
      setError(err.response?.data?.error || err.message || t('try.queryError'));
    } finally {
      setAsking(false);
    }
  };

  const handleExport = async (format, msgIndex) => {
    const assistant = messages[msgIndex];
    if (!assistant || assistant.role !== 'assistant') return;

    // 바로 앞 사용자 질문을 짝으로 저장
    let questionText = '';
    for (let i = msgIndex - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') {
        questionText = messages[i].content;
        break;
      }
    }

    const key = `${msgIndex}-${format}`;
    setExportingKey(key);
    setError('');
    try {
      await downloadExportedFile(format, {
        title: t('try.headline'),
        question: questionText,
        answer: assistant.content,
        sources: assistant.sources || [],
      });
      trackEvent('answer_export', { format });
    } catch (err) {
      setError(await getExportErrorMessage(err, t('try.exportError')));
    } finally {
      setExportingKey('');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f7f9fc', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#1a1a1a' }}>
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} aria-label="back">
            <BackIcon />
          </IconButton>
          <Box
            component="img"
            src="/logo.png"
            alt="koai"
            sx={{ height: 32, objectFit: 'contain', mr: 1 }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            {t('try.title')}
          </Typography>
          <Button
            component={RouterLink}
            to="/register"
            size="small"
            variant="outlined"
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {t('try.saveLater')}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 2, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
          {t('try.headline')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('try.subheadline')}
        </Typography>

        {/* 3단계 */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2.5 }}>
          <StepPill active={activeStep === 1} done={activeStep > 1} index={1} label={t('try.step1')} />
          <StepPill active={activeStep === 2} done={activeStep > 2} index={2} label={t('try.step2')} />
          <StepPill active={activeStep === 3} done={false} index={3} label={t('try.step3')} />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {info && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo('')}>
            {info}
          </Alert>
        )}

        {/* Step 1: 문서 */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            ① {t('try.step1')}
          </Typography>
          <Alert severity="info" sx={{ mb: 2, py: 0 }}>
            {t('try.privacyNote')}
          </Alert>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.hwp,.hwpx"
              style={{ display: 'none' }}
              onChange={handleUpload}
              disabled={uploading || loadingSample}
            />
            <Button
              variant="contained"
              size="large"
              startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || loadingSample}
              sx={{ flex: 1, py: 1.5, fontWeight: 700 }}
            >
              {uploading ? t('try.uploading') : t('try.uploadCta')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={loadingSample ? <CircularProgress size={18} /> : <SparkleIcon />}
              onClick={loadSample}
              disabled={uploading || loadingSample}
              sx={{ flex: 1, py: 1.5, fontWeight: 700 }}
            >
              {loadingSample ? t('try.loadingSample') : t('try.sampleCta')}
            </Button>
          </Stack>

          {(uploading || loadingSample) && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant={uploading && uploadProgress ? 'determinate' : 'indeterminate'}
                value={uploadProgress?.progress ?? 0}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {uploading
                  ? (uploadProgress?.message || t('try.processing'))
                  : t('try.processing')}
              </Typography>
            </Box>
          )}

          {documents.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 2, gap: 0.75 }}>
              {documents.map((doc) => (
                <Chip
                  key={doc.id}
                  icon={<DocIcon />}
                  label={`${doc.name}${doc.chunkCount ? ` (${doc.chunkCount})` : ''}`}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Paper>

        {/* Step 2: 질문 */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            opacity: hasDocs ? 1 : 0.55,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            ② {t('try.step2')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('try.demoHint')}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, gap: 1 }}>
            {demoQuestions.map((q) => (
              <Chip
                key={q}
                label={q}
                clickable
                color="primary"
                variant="outlined"
                disabled={!hasDocs || asking}
                onClick={() => ask(q)}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('try.questionPlaceholder')}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  ask(question);
                }
              }}
              disabled={!hasDocs || asking}
            />
            <Button
              variant="contained"
              onClick={() => ask(question)}
              disabled={!hasDocs || asking || !question.trim()}
              sx={{ minWidth: 48 }}
            >
              {asking ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            </Button>
          </Stack>
        </Paper>

        {/* Step 3: 답변 */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            flex: 1,
            minHeight: 220,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            ③ {t('try.step3')}
          </Typography>

          {messages.length === 0 && !asking && (
            <Typography variant="body2" color="text.secondary">
              {t('try.emptyAnswer')}
            </Typography>
          )}

          <Stack spacing={2}>
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'stretch',
                  maxWidth: msg.role === 'user' ? '85%' : '100%',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                    color: msg.role === 'user' ? 'common.white' : 'text.primary',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {msg.content}
                  </Typography>
                  {msg.role === 'assistant' && <SourceList sources={msg.sources} />}
                  {msg.role === 'assistant' && (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ sm: 'center' }}
                      sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
                      >
                        <SaveIcon sx={{ fontSize: 16 }} />
                        {t('try.saveAs')}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DocIcon />}
                        disabled={!!exportingKey}
                        onClick={() => handleExport('docx', idx)}
                      >
                        {exportingKey === `${idx}-docx` ? t('try.exporting') : t('try.saveWord')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PptIcon />}
                        disabled={!!exportingKey}
                        onClick={() => handleExport('pptx', idx)}
                      >
                        {exportingKey === `${idx}-pptx` ? t('try.exporting') : t('try.savePpt')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ExcelIcon />}
                        disabled={!!exportingKey}
                        onClick={() => handleExport('xlsx', idx)}
                      >
                        {exportingKey === `${idx}-xlsx` ? t('try.exporting') : t('try.saveExcel')}
                      </Button>
                    </Stack>
                  )}
                </Paper>
              </Box>
            ))}
            {asking && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  {t('try.thinking')}
                </Typography>
              </Box>
            )}
            <div ref={chatEndRef} />
          </Stack>
        </Paper>

        {/* 전환 / 보조 CTA */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              trackEvent('signup_click', { from: 'try' });
              navigate('/register');
            }}
            sx={{ fontWeight: 700 }}
          >
            {t('try.continueCta')}
          </Button>
          <Link
            href="mailto:kz4network@gmail.com?subject=koai%20온프레미스%20보안%20상담"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: '0.85rem' }}
          >
            <LockIcon sx={{ fontSize: 16 }} />
            {t('try.onpremCta')}
          </Link>
        </Stack>
      </Container>
    </Box>
  );
}
